import { InputName, InputText, Popover } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { NAMESPACE } from 'locale'
import { selfServeConstants } from 'locale/selfServe'
import { Text } from '@lmc/ui-kit/src/general'
import { useDispatch, useSelector } from 'react-redux'
import { Carer, CarerRole, Home, Name } from '@lmc/ts-common'
import { FC, useState } from 'react'
import { Form } from 'antd'
import Operations from 'operations'
import { OnboardingLayout } from './OnboardingLayout'
import { OnboardingHeading } from './OnboardingHeading'
import { OnboardingText } from './OnboardingText'
import {
  OnboardingButton,
  OnboardingButtonWrapper,
  OnboardingStepIndicator
} from './OnboardingButton'

export type Props = { next: () => void }

export const StepInvite: FC<Props> = ({ next }) => {
  const { t } = useTranslation(NAMESPACE.SELF_SERVE)

  const { t: tGlobal } = useTranslation()

  const home: Home | undefined = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )

  const [loading, setLoading] = useState(false)

  const dispatch = useDispatch()

  const addCarer = (carer: Carer) =>
    dispatch(
      Operations.createListItem({
        listId: 'carers',
        itemId: '',
        json: carer
      })
    )

  const isSelfServe = home.source === 'Self-serve'

  const [form] = Form.useForm()

  const handleSubmit = async ({
    firstname,
    lastname,
    email
  }: {
    firstname: string
    lastname: string
    email: string
  }) => {
    await form.validateFields()
    setLoading(true)
    addCarer({
      name: { first: firstname, last: lastname } as Name,
      email,
      role: CarerRole.careHomeAdmin
    } as Carer).then(() => {
      setLoading(false)
    })
  }

  return (
    <OnboardingLayout>
      <OnboardingHeading level={1}>
        {t(selfServeConstants.INVITE_TEAM)}
      </OnboardingHeading>
      <OnboardingText>{t(selfServeConstants.INVITE_TEAM_DESC)}</OnboardingText>
      <StyledTextLabel>{t(selfServeConstants.NAME_AND_EMAIL)}</StyledTextLabel>
      <Form form={form} onFinish={handleSubmit}>
        <InputNameStyled
          placeholder={{
            firstName: tGlobal(`onboarding.inputs.firstName`),
            lastName: tGlobal(`onboarding.inputs.lastName`)
          }}
        />
        <EmailWrapper>
          <InputStyled
            type="email"
            placeholder={tGlobal(`onboarding.inputs.email`)}
            required
            errorMessage={tGlobal(`onboarding.inputs.emailError`)}
            style={{ maxWidth: 418, width: '100%' }}
          />
        </EmailWrapper>
      </Form>
      <OnboardingButtonWrapper>
        <Popover content={t(selfServeConstants.PLEASE_FILL)}>
          <OnboardingButton
            variant="default"
            size="large"
            onClick={form.submit}
            isLoading={loading}
          >
            {t(selfServeConstants.SEND_INVITE)}
          </OnboardingButton>
        </Popover>
        <OnboardingStepIndicator>
          {t(selfServeConstants.STEP_X_OF_X, { from: 2, to: 3 })}
        </OnboardingStepIndicator>
      </OnboardingButtonWrapper>
      {isSelfServe && (
        <WithoutWrapper onClick={next}>
          <StyledTextBottom level={1}>
            {t(selfServeConstants.CONTINUE_WITHOUT)}
          </StyledTextBottom>
        </WithoutWrapper>
      )}
    </OnboardingLayout>
  )
}

const WithoutWrapper = styled.div`
  z-index: 10;
  margin-top: 30px;
`

const StyledTextLabel = styled(Text)`
  color: ${({ theme }) => theme.palette.darkpurple};
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 20px;
`

const StyledTextBottom = styled(Text)`
  color: ${({ theme }) => theme.palette.lmcPink};
  text-decoration: underline;
  cursor: pointer;
`

const EmailWrapper = styled.div`
  margin-top: 15px;
`

const InputNameStyled = styled(InputName)`
  .ant-input::placeholder {
    color: ${({ theme }) => theme.color.neutral.color5} !important;
  }
`

const InputStyled = styled(InputText)`
  .ant-input::placeholder {
    color: ${({ theme }) => theme.color.neutral.color5} !important;
  }
`
